/* ============================================================
   pdf-flipbook.js — PDF-Flipbook (echter Blätter-Effekt)
   Spec: docs/superpowers/specs/2026-07-29-pdf-flipbook-design.md

   Rendert ein PDF (PDF.js) Seite für Seite auf Canvas und übergibt die
   Seitenbilder an StPageFlip (Blätter-Engine). Das Buch füllt die verfügbare
   Fläche maximal aus — die Section-/Bühnenhöhe ist ausschlaggebend. Im Vollbild
   nutzt es die gesamte Bildschirmhöhe. Kein Zoom. Steuerung: Pfeile, Tastatur,
   Seitenzähler, Vollbild. Auf schmalen Screens (Mobil) Einzelseite.

   Globals: window.pdfjsLib (pdf.min.js), window.St.PageFlip (page-flip.browser.js)
   ============================================================ */
(function () {
  'use strict';

  var RENDER_MAX_PX = 1500;    // Deckel für die lange Kante pro Seite (Speicher/Performance)
  var MOBILE_QUERY = '(max-width: 767px)';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-pdf-flipbook]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.dataset.pfInit === '1') return;
      el.dataset.pfInit = '1';
      try {
        new Flipbook(el);
      } catch (e) {
        showError(el, 'Das Flipbook konnte nicht gestartet werden.');
        if (window.console) console.error('[pdf-flipbook]', e);
      }
    });
  }

  function Flipbook(root) {
    this.root = root;
    this.url = root.dataset.pdfUrl;
    this.workerUrl = root.dataset.workerUrl;
    this.doublePage = root.dataset.double !== 'false';

    this.viewport = root.querySelector('[data-pf-viewport]');
    this.scaler = root.querySelector('[data-pf-scaler]');
    this.mount = root.querySelector('[data-pf-mount]');
    this.statusEl = root.querySelector('[data-pf-status]');
    this.progressEl = root.querySelector('[data-pf-progress]');
    this.counterEl = root.querySelector('[data-pf-counter]');
    this.btnPrev = root.querySelector('[data-pf-prev]');
    this.btnNext = root.querySelector('[data-pf-next]');
    this.btnFs = root.querySelector('[data-pf-fullscreen]');

    if (!this.url) { showError(root, 'Kein PDF hinterlegt.'); return; }
    if (!window.pdfjsLib || !window.St || !window.St.PageFlip) {
      showError(root, 'Flipbook-Bibliotheken nicht geladen.');
      return;
    }

    this.bindControls();
    this.load();
  }

  Flipbook.prototype.load = function () {
    var self = this;
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerUrl;

    window.pdfjsLib.getDocument({ url: this.url }).promise.then(function (pdf) {
      self.pdf = pdf;
      return self.renderAllPages(pdf);
    }).then(function (images) {
      self.initFlip(images);
    }).catch(function (err) {
      if (window.console) console.error('[pdf-flipbook]', err);
      showError(self.root, 'Das PDF konnte nicht geladen werden. Bitte die hinterlegte PDF-URL prüfen.');
    });
  };

  // Rendert alle Seiten nacheinander zu Bild-Daten-URLs, mit Fortschrittsanzeige.
  Flipbook.prototype.renderAllPages = function (pdf) {
    var self = this;
    var total = pdf.numPages;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var images = [];

    function renderPage(n) {
      return pdf.getPage(n).then(function (page) {
        var base = page.getViewport({ scale: 1 });
        var longest = Math.max(base.width, base.height);
        var scale = (RENDER_MAX_PX * dpr) / longest;
        if (scale > dpr * 2) scale = dpr * 2;
        var vp = page.getViewport({ scale: scale });

        var canvas = document.createElement('canvas');
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        return page.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
          images.push(canvas.toDataURL('image/jpeg', 0.85));
          if (self.progressEl) self.progressEl.textContent = 'Seite ' + n + ' von ' + total;
          page.cleanup && page.cleanup();
        });
      });
    }

    var chain = Promise.resolve();
    for (var i = 1; i <= total; i++) {
      (function (n) { chain = chain.then(function () { return renderPage(n); }); })(i);
    }
    return chain.then(function () { return images; });
  };

  Flipbook.prototype.initFlip = function (images) {
    var self = this;
    if (!images.length) { showError(this.root, 'Das PDF enthält keine Seiten.'); return; }

    var probe = new Image();
    probe.onload = function () {
      self.images = images;
      self.ratio = probe.naturalHeight / probe.naturalWidth || 1.414; // Höhe/Breite
      self.buildFlip();
      self.bindRelayout();
      if (self.statusEl) self.statusEl.hidden = true;
    };
    probe.onerror = function () { showError(self.root, 'Seiten konnten nicht dargestellt werden.'); };
    probe.src = images[0];
  };

  // Einzelseite gewünscht? → wenn Doppelseite-Setting aus ODER schmaler Screen (Mobil)
  Flipbook.prototype.wantSingle = function () {
    if (!this.doublePage) return true;
    return window.matchMedia(MOBILE_QUERY).matches;
  };

  // Seitengröße für eine gegebene Fläche berechnen (contain, ratio = Höhe/Breite).
  Flipbook.prototype.pageSize = function (single, availW, availH) {
    var across = single ? 1 : 2;
    var pageW = Math.min(availW / across, availH / this.ratio);
    return { w: Math.round(pageW), h: Math.round(pageW * this.ratio) };
  };

  // Das Buch wird EINMAL in (potenzieller) Bildschirmgröße gerendert und danach nur
  // noch per CSS-transform in die jeweilige Bühne skaliert (siehe fitToStage). Da wir
  // von der Bildschirmgröße nur herunterskalieren, bleibt es immer scharf; im Vollbild
  // ist es ~1:1. Kein destroy/rebuild (das rendert in dieser StPageFlip-Version kein
  // Canvas mehr) → Inhalt bleibt sichtbar bei Resize/Vollbild.
  Flipbook.prototype.buildFlip = function () {
    var self = this;
    var single = this.wantSingle();
    this.singleMode = single;

    var screenW = (window.screen && window.screen.width) || window.innerWidth || 1280;
    var screenH = (window.screen && window.screen.height) || window.innerHeight || 800;
    var buildW = Math.max(this.viewport.clientWidth, screenW);
    var buildH = Math.max(this.viewport.clientHeight, screenH);
    var size = this.pageSize(single, buildW - 8, buildH - 8);

    var pf = new window.St.PageFlip(this.mount, {
      width: size.w,
      height: size.h,
      size: 'fixed',
      showCover: true,
      usePortrait: single,
      maxShadowOpacity: 0.5,
      mobileScrollSupport: false,
      clickEventForward: false,
      useMouseEvents: true
    });

    pf.loadFromImages(this.images);
    this.pf = pf;
    this.total = pf.getPageCount();
    pf.on('flip', function (e) { self.updateCounter(e.data); });
    pf.on('changeState', function () { self.updateCounter(pf.getCurrentPageIndex()); });
    this.updateCounter(pf.getCurrentPageIndex());
    var self2 = this;
    this.fitToStage();
    // .stf__parent bekommt seine Maße evtl. erst nach dem ersten Render → nachziehen,
    // danach Transition aktivieren (is-ready) für sanftes Resize/Vollbild.
    setTimeout(function () {
      self2.fitToStage();
      self2.root.classList.add('is-ready');
    }, 60);
  };

  // Buch passgenau in die aktuelle Bühne skalieren (füllt sie maximal; nur herunter).
  // Skaliert den Scaler-Wrapper (nicht den Mount — den kontrolliert StPageFlip).
  // Die echte, unskalierte Buchgröße wird an .stf__parent gemessen.
  Flipbook.prototype.fitToStage = function () {
    if (!this.pf || !this.scaler) return;
    var parent = this.mount.querySelector('.stf__parent');
    var natW = parent ? parent.offsetWidth : this.mount.offsetWidth;
    var natH = parent ? parent.offsetHeight : this.mount.offsetHeight;
    if (natW < 10 || natH < 10) return;
    var availW = this.viewport.clientWidth - 8;
    var availH = this.viewport.clientHeight - 8;
    if (availW < 40 || availH < 40) return;
    var scale = Math.min(availW / natW, availH / natH);
    this.scaler.style.transformOrigin = 'center center';
    this.scaler.style.transform = 'scale(' + (Math.round(scale * 1000) / 1000) + ')';
  };

  Flipbook.prototype.bindRelayout = function () {
    var self = this;
    var t = null;
    function schedule(delay) {
      if (t) clearTimeout(t);
      t = setTimeout(function () { self.fitToStage(); }, delay || 120);
    }
    window.addEventListener('resize', function () { schedule(120); });
    // Vollbild braucht evtl. einen Frame, bis das Layout steht → mehrfach nachmessen.
    function onFs() {
      requestAnimationFrame(function () { self.fitToStage(); });
      schedule(250);
    }
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
  };

  Flipbook.prototype.updateCounter = function (index) {
    if (!this.pf) return;
    var current = (typeof index === 'number' ? index : this.pf.getCurrentPageIndex()) + 1;
    if (this.counterEl) this.counterEl.textContent = current + ' / ' + this.total;
    if (this.btnPrev) this.btnPrev.disabled = current <= 1;
    if (this.btnNext) this.btnNext.disabled = current >= this.total;
  };

  Flipbook.prototype.bindControls = function () {
    var self = this;
    if (this.btnPrev) this.btnPrev.addEventListener('click', function () { self.pf && self.pf.flipPrev(); });
    if (this.btnNext) this.btnNext.addEventListener('click', function () { self.pf && self.pf.flipNext(); });

    this.root.addEventListener('keydown', function (e) {
      if (!self.pf) return;
      if (e.key === 'ArrowLeft') { self.pf.flipPrev(); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { self.pf.flipNext(); e.preventDefault(); }
    });
    this.root.setAttribute('tabindex', '-1');

    if (this.btnFs) this.btnFs.addEventListener('click', function () { self.toggleFullscreen(); });
  };

  Flipbook.prototype.toggleFullscreen = function () {
    var el = this.root;
    var doc = document;
    if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen || function () {}).call(el);
    } else {
      (doc.exitFullscreen || doc.webkitExitFullscreen || function () {}).call(doc);
    }
  };

  function showError(root, msg) {
    var status = root.querySelector('[data-pf-status]');
    if (!status) return;
    status.hidden = false;
    status.innerHTML = '<p class="pdf-flipbook__error">' + msg + '</p>';
  }

  // Init erst NACH allen Prototype-Definitionen aufrufen (Scripts laufen mit defer).
  ready(boot);
})();
